import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import React from "react";
import { Link } from "react-router-dom";
import * as uuid from "uuid";
import { ReserveShiftsReportList, ReserveShiftsReportRow } from "../../../api";
import {
    LoadingTableElement,
    NullTableElement,
} from "../../../components/table/nullTable";
import { Order } from "../../../components/table/sort";
import {
    ChangePageCond,
    ChangeSortCond,
    HeadCell,
    SortableTable,
} from "../../../components/table/sortableTable";
import {
    formatFloatToPercentage,
    convertFloatToPercentage,
    convertMinToDisplayFormat,
    convertToShiftDate,
    formatPaymentAmountDisplay,
} from "../../../services/common/convert";
import { getDriverById } from "../../../services/common/drivers";
import {
    resetTripReports,
    selectTripsReport,
} from "../../../services/reserve/trips_report";
import * as settings from "../../../settings";
import { commonState, store } from "../../../store";
import { colors } from "../../../theme";
import { toInlineBatsuMark } from "../../../utils/formatting";
import { ReserveShiftsState, reserveShiftsSlice } from "../store";

export interface Props {
    commonState: commonState;
    reserveShifts: ReserveShiftsState;
}

export const convertShiftsReportToDataTableFormat = (
    shiftsReportList: ReserveShiftsReportList | null,
    dummyEnabled: boolean
): Row[] => {
    if (!shiftsReportList || !shiftsReportList.shiftReports) {
        return [];
    }

    const _data = shiftsReportList.shiftReports.map(
        (rec: ReserveShiftsReportRow) => {
            let officeName = rec.officeName;
            if (dummyEnabled) {
                officeName = rec.officeName
                    ? rec.officeName
                    : `${rec.officeId}営業所`;
            }

            const elmt = getDriverById(rec.driverId, rec.officeId);
            const drivername = elmt ? elmt.driverName : rec.driverName;

            const needGuidanceHermes =
                rec.needGuidanceHermes == "required" ? 1 : 0;

            const needGuidanceEarnings =
                rec.needGuidanceEarnings == "required" ? 1 : 0;

            const needGuidanceBreaks =
                rec.needGuidanceBreaks == "required" ? 1 : 0;
            return {
                shift_date: convertToShiftDate(rec.shiftDate),
                row_id: uuid.v4(),
                office: officeName,
                driver_id: rec.driverCode,
                driver_name: drivername,
                need_guidance_stop: needGuidanceBreaks,
                need_guidance_navi_usage: needGuidanceHermes,
                need_guidance_earnings: needGuidanceEarnings,
                shift_length: rec.shiftLengthMin,
                stop_length: rec.breaksLengthMin,
                pause_length: rec.pauseCarRequestLengthMin,
                acceptance_rate: rec.acceptanceRate,
                navi_usage_rate: rec.naviUsageRate,
                hourly_earnings: rec.timeEarnings,
                next_page_links: <LinkToNext selectedRecord={rec} />,
            } as Row;
        }
    );
    return _data;
};

interface LinkProps {
    selectedRecord: ReserveShiftsReportRow;
}

const LinkToNext: React.FC<LinkProps> = (props: LinkProps) => {
    const shift_id = props.selectedRecord.shiftId ?? "";
    const shift_date = props.selectedRecord.shiftDate;
    const office_id = props.selectedRecord.officeId;
    const driver_id = props.selectedRecord.driverId;
    const needGuidanceHeader = props.selectedRecord.needGuidanceHermes;
    const driver_code = props.selectedRecord.driverCode;

    return (
        <Box>
            <MenuItem
                component={Link}
                sx={{
                    color: colors.blue.main,
                    p: 0.5,
                    pl: 3,
                    minHeight: "0px",
                }}
                onClick={async (x) => {
                    if (shift_date && office_id && driver_id) {
                        console.debug(
                            "Selected a shift:",
                            props.selectedRecord
                        );
                        await resetTripReports();

                        const trips = await selectTripsReport(
                            shift_id,
                            office_id,
                            needGuidanceHeader,
                            driver_code,
                            props.selectedRecord.naviUsageRate
                        );
                    } else {
                        console.log(
                            "One of these was empty... shift_date, office_id, driver_id",
                            props.selectedRecord
                        );
                    }
                }}
                to="/reserve/trips"
            >
                ナビ利用詳細
            </MenuItem>
        </Box>
    );
};

export const Table: React.FC<Props> = (props: Props) => {
    console.debug(
        "DriverUsage shifts...",
        props.reserveShifts,
        props.commonState.driverNames
    );

    const _data = convertShiftsReportToDataTableFormat(
        props.reserveShifts.shiftReports,
        props.commonState.versions.dummy_enabled
    );

    if (!props.reserveShifts.shiftReports) {
        return <LoadingTableElement />;
    }

    if (
        props.reserveShifts.shiftReports.shiftReports &&
        props.reserveShifts.shiftReports.shiftReports.length == 0
    ) {
        return <NullTableElement />;
    }

    // ソート条件を保持するコールバック関数
    const callbackS: ChangeSortCond = (key: string, order: Order): void => {
        store.dispatch(
            reserveShiftsSlice.actions.update_sort_cond([key, order])
        );
    };
    // 表示しているページを保持するコールバック関数
    const callbackP: ChangePageCond = (no: number, numRows: number): void => {
        store.dispatch(
            reserveShiftsSlice.actions.update_page_cond([no, numRows])
        );
    };

    const dc = store.getState().reserveShifts.displayCond;
    return (
        <SortableTable
            tableHeaders={headCells}
            tableData={_data}
            tableProps={props}
            displayCond={dc}
            rowIdField={"row_id"}
            onRowClickMethod={(r: any) => {}}
            slimRows={true}
            callbackChangeSortCond={callbackS}
            callbackChangePageCond={callbackP}
        />
    );
};

const headCells: HeadCell[] = [
    {
        label: "乗務日",
        id: "shift_date",
        numeric: false,
        disablePadding: true,
        grow: 3,
    },
    {
        label: "営業所",
        id: "office",
        numeric: false,
        grow: 1,
        disablePadding: true,
        wrap: true,
    },
    {
        label: "乗務員番号",
        id: "driver_id",
        numeric: false,
        // align: "right",
        headerAlignRight: false,
        disablePadding: true,
        grow: 2,
        wrap: true,
    },
    {
        label: "乗務員氏名",
        id: "driver_name",
        numeric: false,
        grow: 2,
        wrap: true,
        disablePadding: true,
    },
    {
        label: "乗務時間",
        id: "shift_length",
        align: "right",
        numeric: true,
        grow: 1,
        headerAlignRight: true,
        disablePadding: true,
        format: (x: number, row: any) => {
            return convertMinToDisplayFormat(x);
        },
    },
    {
        label: "配車停止時間",
        id: "pause_length",
        numeric: true,
        grow: 2,
        align: "right",
        disablePadding: true,
        headerAlignRight: true,
        format: (x: number, row: any) => {
            return convertMinToDisplayFormat(row.pause_length);
        },
    },
    {
        label: "配車承諾率",
        id: "acceptance_rate",
        numeric: true,
        align: "right",
        headerAlignRight: true,
        disablePadding: true,
        grow: 1,
        format: (x: number, row: any) => {
            if (x === -1) {
                // -1 はNULL(配車依頼が全くない)を意味する
                return <Box />;
            }
            return formatFloatToPercentage(x);
        },
    },
    {
        label: "ナビ利用率",
        id: "navi_usage_rate",
        numeric: true,
        align: "right",
        headerAlignRight: true,
        disablePadding: true,
        grow: 1,
        format: (x: number, row: any) => {
            if (x === settings.naviUsageBlankVal) {
                return <Box />;
            }
            return toInlineBatsuMark(
                convertFloatToPercentage(x),
                row.need_guidance_navi_usage
            );
        },
    },
    {
        label: "時間営収",
        id: "hourly_earnings",
        numeric: true,
        headerAlignRight: true,
        disablePadding: true,

        grow: 2,
        align: "right",
        format: (x: number, row: any) => {
            return toInlineBatsuMark(
                formatPaymentAmountDisplay(x),
                row.need_guidance_earnings,
                "円"
            );
        },
    },
    {
        label: "",
        id: "next_page_links",
        numeric: true,
        grow: 2,
    },
];

type Row = {
    shift_date?: string;
    office?: string;
    driver_id?: string;
    driver_name?: string;
    need_guidance_stop?: number;
    need_guidance_navi_usage?: number;
    need_guidance_earnings?: number;
    shift_length?: number;
    stop_length?: number;
    pause_length?: number;
    acceptance_rate?: number;
    navi_usage_rate?: number;
    hourly_earnings?: number;
    next_page_links?: JSX.Element;
};
